import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./common/Logo";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const navLinks = [
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-slate-900/80 backdrop-blur-lg shadow-lg border-b border-slate-800"
            : "bg-transparent"
        }`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex justify-between items-center text-white">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center hover:opacity-90 transition-opacity z-50"
            aria-label="Sportofolo home"
          >
            <Logo variant="dark" className="h-10 sm:h-12 w-auto" />
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-5 lg:gap-8 text-sm font-medium">
            {navLinks.map((link) => {
              const active = location.pathname.startsWith(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative group transition-colors ${
                    active ? "text-indigo-400" : "text-gray-200"
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute left-0 bottom-[-6px] h-[2px] w-0 bg-indigo-400 rounded-full transition-all duration-300 group-hover:w-full ${
                      active ? "w-full" : "w-0"
                    }`}
                  ></span>
                </Link>
              );
            })}

            {/* Auth CTAs */}
            <div className="flex items-center gap-3 pl-3 lg:pl-5 ml-1 border-l border-white/15">
              <Link
                to="/login"
                className="text-gray-200 hover:text-white transition-colors"
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="bg-white text-slate-900 px-4 py-2 rounded-full hover:bg-gray-100 transition-colors text-sm font-semibold whitespace-nowrap"
              >
                Sign Up
              </Link>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden flex items-center justify-center w-10 h-10 text-gray-200 hover:text-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 rounded-lg transition-colors z-50"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </nav>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Mobile Menu Panel */}
            <motion.div
              className="fixed top-0 right-0 bottom-0 w-full xs:w-80 bg-slate-900 shadow-2xl z-40 md:hidden overflow-y-auto"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
            >
              <div className="flex flex-col h-full pt-24 pb-8 px-6">
                {/* Mobile Nav Links */}
                <nav className="flex flex-col space-y-1">
                  {navLinks.map((link) => {
                    const active = location.pathname.startsWith(link.to);
                    return (
                      <Link
                        key={link.to}
                        to={link.to}
                        className={`flex items-center px-4 py-4 rounded-lg text-base font-medium transition-colors ${
                          active
                            ? "bg-indigo-500/20 text-indigo-400"
                            : "text-gray-200 hover:bg-slate-800 hover:text-white"
                        }`}
                      >
                        {link.label}
                      </Link>
                    );
                  })}
                </nav>

                {/* Mobile Auth CTAs */}
                <div className="mt-6 space-y-3">
                  <Link
                    to="/login"
                    className="block w-full text-center text-white border border-white/30 px-6 py-3 rounded-full hover:bg-white/10 transition-colors font-medium"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="block w-full text-center bg-white text-slate-900 px-6 py-3 rounded-full hover:bg-gray-100 transition-colors font-semibold"
                  >
                    Sign Up
                  </Link>
                </div>

                {/* Mobile Menu Footer */}
                <div className="mt-auto pt-6 border-t border-slate-800">
                  <p className="text-gray-400 text-sm text-center">
                    © {new Date().getFullYear()} Sportofolo
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
